import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { classes, Root } from './CDS'
import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  IconButtonProps,
  Popover,
  Typography,
} from '@mui/material'
import { red } from '@mui/material/colors'

import { styled } from '@mui/material/styles'

import {
  Share as ShareIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'

import { windowOpen } from '../../../adapters/windowUtils'
import React from 'react'
import { PostItemMetaInfo, ServerPostItem } from '../../../adapters/FeedAdapter'
import { useResizeListener } from '../../../util/ReactReusable'

// Helper function for highlighting text (re-add this)
const highlightText = (text: string | undefined, keyword: string | undefined): React.ReactNode => {
  if (!keyword || keyword.trim() === '' || !text) {
    return text
  }
  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedKeyword})`, 'gi')
  const parts = text.split(regex)

  return parts.map((part, index) =>
    regex.test(part) && part.toLowerCase() === keyword.toLowerCase() ? <mark key={index}>{part}</mark> : part
  )
}

export type PostItemViewNeeds = {
  isSmallDevice: boolean
  onLikeUnlikeClick: (itemId: string) => Promise<void> | undefined
  onLoadRequest: Function
}
// exported only for mvu feed
export type PostItemViewProps = PostItemViewNeeds & {
  postItem: ServerPostItem
  searchKeyword?: string // Re-add this prop
}

const THUMB_HEIGHT = '100%'
const MAX_HEIGHT = 600

interface ThumbnailProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  loaded: boolean
  isSmallDevice: boolean
}

// Bug: onLoadStart is not passed to img https://github.com/facebook/react/issues/20330
// chrome bug: loadstart event is not fired when <img> resource starts loading. https://issues.chromium.org/issues/41157691
const CardThumbnail = styled((props: ThumbnailProps) => {
  const { loaded, isSmallDevice, onLoad, ...other } = props
  let onLoad2 = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // console.warn('img:loading ' + props.src, e.target)
    if (onLoad) onLoad(e)
  }
  return (
    <div
      style={{
        height: THUMB_HEIGHT,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <img
        alt="author thumbnail"
        onLoad={e => {
          onLoad2(e)
        }}
        {...other}
        loading="lazy"
        style={{
          maxWidth: '100%',
          margin: '0 auto',
          display: 'block',
        }}
      />
    </div>
    // <LazyLoad once data-file="CDS" height={THUMB_HEIGHT} offset={800} placeholder={<Skeleton sx={{ height: "100%" }} animation="wave" variant="rectangular" />}>
    // </LazyLoad>
  )
})(({ theme, loaded }) => ({
  display: 'block',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  width: '100%',
  objectFit: 'contain',
  height: THUMB_HEIGHT,
  [theme.breakpoints.down('md')]: {
    height: '100%',
    width: '100%',
    margin: '0 auto',
  },
  maxHeight: MAX_HEIGHT,
  opacity: !loaded ? 0 : 1,
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.enteringScreen,
  }),
}))

interface ShareProps extends Omit<IconButtonProps, 'title'> {
  anchorEl: any
  onOpen: React.MouseEventHandler<HTMLButtonElement>
  onClose: () => void
}

const Share = styled((props: ShareProps) => {
  const { anchorEl, onOpen, onClose, onClick, ...other } = props
  return (
    <React.Fragment>
      <IconButton title="Share link" {...other} data-file="ServerPostComponent" onClick={onOpen}>
        <ShareIcon />
      </IconButton>
      <Popover
        id={Boolean(anchorEl) ? 'share-popover' : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose as () => void}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {props.children}
      </Popover>
    </React.Fragment>
  )
})(() => ({}))

interface LikeProps extends IconButtonProps {
  id: string
  onLoadRequest: Function
  likeInfo: Omit<PostItemMetaInfo, 'totalClicks'> | undefined
}

// https://bobbyhadz.com/blog/react-check-if-element-in-viewport
// https://spacejelly.dev/posts/how-to-trigger-a-function-when-scrolling-to-an-element-in-react-intersection-observer
// https://stackoverflow.com/questions/58328428/trigger-event-when-a-each-div-has-been-scrolled-into-view-and-update-state
// https://dev.to/jmalvarez/check-if-an-element-is-visible-with-react-hooks-27h8
function useIsInViewport<T extends Element>(ref: React.MutableRefObject<T | null>) {
  const [isIntersecting, setIsIntersecting] = useState(false)

  const observer = useMemo(() => new IntersectionObserver(([entry]) => setIsIntersecting(entry.isIntersecting)), [])

  useEffect(() => {
    if (ref.current != null) observer.observe(ref.current)

    return () => {
      observer.disconnect()
    }
  }, [ref, observer])

  return isIntersecting
}
// this may be better off lifted and split into another component that allows disposal of the viewport stuff once loaded
const Like = styled((props: LikeProps) => {
  const { id, onClick, disabled, onLoadRequest, likeInfo, ...other } = props
  const loaded = props.likeInfo !== undefined
  const userLiked = props.likeInfo && props.likeInfo.iLike === true

  let likeRef = useRef<HTMLDivElement>(null)
  // on scroll into view, triggering like loading options:
  const isInViewport = useIsInViewport<HTMLDivElement>(likeRef)
  const [isLoading, setIsLoading] = useState(loaded)

  useEffect(() => {
    if (loaded || isLoading) return
    if (!isInViewport) return
    setIsLoading(true)
    // console.info('loading like info for', id);
    onLoadRequest()
  }, [loaded, isLoading, onLoadRequest, isInViewport])
  // console.log('rendering like:', props.id, props.likeInfo);

  return (
    <React.Fragment>
      <div
        ref={likeRef}
        key={props.id}
        title={loaded && !disabled ? (userLiked ? 'Unlike Post' : 'Like Post') : 'Likes disabled or failed to load'}
      >
        <IconButton
          {...other}
          data-file="ServerPostComponent"
          disabled={disabled || !loaded}
          onClick={props.likeInfo ? props.onClick : () => {}}
        >
          {userLiked ? <ThumbUpIcon /> : <ThumbUpOIcon />}
          <div className={classes.actionBarIconText}>{(props.likeInfo && props.likeInfo.likes) ?? ''}</div>
        </IconButton>
        {props.children}
      </div>
    </React.Fragment>
  )
})(() => ({}))

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean
}
const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props
  return (
    <IconButton {...other}>
      <ExpandMoreIcon />
    </IconButton>
  )
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto !important',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}))

// exported only for mvu feed
export const PostItemView = (props: PostItemViewProps) => {
  // Used for details flyout
  const [expanded, setExpanded] = useState(false)

  // Used for fading in thumbnails when they are loaded
  const [thumbLoaded, setThumbLoaded] = useState<boolean>(false)

  // Used for favorite button
  const [likeUnlikeInFlight, setLikeUnlikeInFlight] = useState<boolean>(false) //TODO replace with backend call

  // Used for pop out of share button
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [copied, setCopied] = useState<boolean>(false)
  let postItem = props.postItem

  const onPostItemLinkClick = useCallback(() => windowOpen(postItem.link, '_blank', ''), [postItem])

  const handleCopy = () => {
    navigator.clipboard.writeText(postItem.link)
    setCopied(true)
  }
  const handleOpenShare = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
    setCopied(false)
  }

  // Collapse or expand description to properly use space
  // consider lifting the window parts of this into windowUtils
  const handleResize = useCallback(() => {
    if (window.innerWidth < 700 && expanded) {
      setExpanded(false)
    }
  }, [expanded])

  useResizeListener(handleResize)

  const onLikeUnlikeClick = useCallback(
    async (itemId: string) => {
      if (!postItem.postItemMetaInfo) return
      if (likeUnlikeInFlight) return
      // console.log('onLikeUnlikeClick');
      setLikeUnlikeInFlight(true)
      await props.onLikeUnlikeClick(itemId)
      // the below call assumes the above call blocks, instead of being async
      // console.log('fin onLikeUnlikeClick');
      setLikeUnlikeInFlight(false)
    },
    [likeUnlikeInFlight, postItem.postItemMetaInfo, props]
  )
  const onRefreshMetaClick = useCallback(
    // async (itemId: string) => {
    async () => {
      if (!postItem.postItemMetaInfo) return
      console.log('onRefreshMetaClick')
      props.onLoadRequest()
    },
    [postItem.postItemMetaInfo, props]
  )
  const hasContent = postItem.summary

  return (
    <Root data-file="ServerPostComponent" data-method="PostItem">
      <Card variant="outlined" className={classes.container}>
        <CardHeader
          avatar={
            postItem.hasAuthor && postItem.authorImageUrl ? (
              <img
                src={postItem.authorImageUrl}
                style={{ maxHeight: '40px' }}
                alt={`${postItem.providerDisplayName} profile`}
              />
            ) : (
              <Avatar sx={{ bgcolor: red[500] }} aria-label="avatar">
                {postItem.providerDisplayName.substring(0, 1).toUpperCase()}
              </Avatar>
            )
          }
          title={highlightText(postItem.providerDisplayName, props.searchKeyword)}
          subheader={<span title={postItem.dateTitle}>{postItem.date}</span>}
        />
        <CardThumbnail
          className={classes.hover}
          loaded={thumbLoaded}
          isSmallDevice={props.isSmallDevice}
          onLoad={() => setThumbLoaded(true)}
          src={postItem.thumbnail}
          onClick={() => windowOpen(postItem.link, '_blank', '')}
        />
        <CardContent>
          <Typography variant="h6" color="text.secondary">
            {highlightText(postItem.title, props.searchKeyword)}
          </Typography>
        </CardContent>
        <CardActions disableSpacing>
          <Share aria-label="share" anchorEl={anchorEl} onOpen={handleOpenShare} onClose={() => setAnchorEl(null)}>
            {!copied ? (
              <div className={classes.share} onClick={handleCopy}>
                <IconButton aria-label="click to copy content">
                  <ContentCopyIcon />
                </IconButton>
                <Typography align="left" sx={{ p: 2, color: 'currentcolor' }}>
                  Copy Link
                </Typography>
              </div>
            ) : (
              <div className={`${classes.share} ${classes.sharePostCopy}`}>
                <CheckIcon sx={{ fill: 'white' }} />
                <Typography align="left" sx={{ p: 2 }}>
                  Link Copied
                </Typography>
              </div>
            )}
          </Share>
          <Like
            aria-label="like"
            id={postItem.id}
            likeInfo={postItem.postItemMetaInfo}
            onClick={likeUnlikeInFlight ? () => {} : () => onLikeUnlikeClick(postItem.id)}
            onLoadRequest={props.onLoadRequest}
          ></Like>
          <IconButton
            data-file="ServerPostComponent"
            title="Click counter, click to read more"
            onClick={onPostItemLinkClick}
            disabled={!postItem.postItemMetaInfo}
          >
            <VisibilityIcon />
            <div className={classes.actionBarIconText}>
              {(postItem.postItemMetaInfo && postItem.postItemMetaInfo.totalClicks) ?? ''}
            </div>
          </IconButton>
          <IconButton
            data-file="ServerPostComponent"
            title="Refresh like and click count information"
            disabled={!postItem.postItemMetaInfo}
            onClick={postItem.postItemMetaInfo ? () => onRefreshMetaClick() : () => {}}
          >
            <RefreshIcon />
          </IconButton>
          {hasContent && (
            <ExpandMore
              expand={expanded}
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              aria-label="show more"
            />
          )}
        </CardActions>
        {hasContent && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CardContent>
              <Typography padding="0 10px" align="justify" variant="body1" sx={{ wordWrap: 'anywhere' }}>
                {highlightText(postItem.summary, props.searchKeyword)}
              </Typography>
              <div className={classes.blur} />
              <Typography
                sx={{ textAlign: 'center', display: 'block', cursor: 'pointer' }}
                variant="button"
                onClick={onPostItemLinkClick}
              >
                Click to read more
              </Typography>
            </CardContent>
          </Collapse>
        )}
      </Card>
    </Root>
  )
}
